import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getProducts, createProduct, updateProduct, deleteProduct, reorderProducts, bulkCreateProducts,
  getCategories, createCategory, updateCategory, deleteCategory, reorderCategoriesApi,
  getPackagings, createPackaging, updatePackaging, deletePackaging, reorderPackagings
} from '../services/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

// Greek-friendly normalize: lowercase + remove accents
const normalizeText = (str) => (str || '').toString().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const emptyProductForm = { name: '', category_id: '', price: '', packaging: '' };
const MAX_BULK_PRODUCTS = 100;

export default function Products() {
  const { selectedFactory, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [savingProduct, setSavingProduct] = useState(false);

  // Category management modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [catEditId, setCatEditId] = useState(null);
  const [catEditName, setCatEditName] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  // Packaging management modal
  const [packagings, setPackagings] = useState([]);
  const [showPkgModal, setShowPkgModal] = useState(false);
  const [pkgEditId, setPkgEditId] = useState(null);
  const [pkgEditName, setPkgEditName] = useState('');
  const [newPkgName, setNewPkgName] = useState('');
  const [savingPkg, setSavingPkg] = useState(false);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCatId, setUploadCatId] = useState('');
  const [uploadParsed, setUploadParsed] = useState([]); // { name, packaging, price, error? }
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadSaving, setUploadSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Category modal drag state
  const modalDragItem = useRef(null);
  const modalDragBackup = useRef(null);
  const modalLastTarget = useRef(null);
  const modalDropDone = useRef(false);
  const [modalDraggingId, setModalDraggingId] = useState(null);

  // Product drag state (main page)
  const dragType = useRef(null);
  const dragItem = useRef(null);
  const dragCatName = useRef(null);
  const dragBackup = useRef(null);
  const lastTarget = useRef(null);
  const dropDone = useRef(false);
  const [draggingProductId, setDraggingProductId] = useState(null);

  // Category drag state (main page) — DROP INDICATOR approach
  const catDragItem = useRef(null);
  const catDropDone = useRef(false);
  const [draggingCatId, setDraggingCatId] = useState(null);
  const [catDropIndicator, setCatDropIndicator] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const [currentMatch, setCurrentMatch] = useState(-1);
  const [highlightKey, setHighlightKey] = useState(null); // 'prod:id' or 'cat:id'
  const rowRefs = useRef({}); // keys: 'prod:id' or 'cat:id'

  const canManage = user?.role === 'super_admin' || user?.role === 'manager';

  const loadAll = () => {
    if (!selectedFactory) return;
    setLoading(true);
    Promise.all([
      getProducts(selectedFactory.id, showInactive),
      getCategories(selectedFactory.id),
      getPackagings()
    ])
      .then(([prodRes, catRes, pkgRes]) => {
        setProducts(prodRes.data);
        setCats(catRes.data);
        setPackagings(pkgRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, [selectedFactory, showInactive]);

  const sortedCatNames = cats.map(c => c.name);
  const getProductsByCat = (catName) => products.filter(p => (p.category || '') === catName);

  // Packaging helpers (use state-loaded list)
  const packagingNames = packagings.map(p => p.name.toLowerCase());
  const packagingLabel = (val) => {
    if (!val) return '(κενό)';
    // Match case-insensitively against current packagings
    const match = packagings.find(p => p.name.toLowerCase() === String(val).toLowerCase());
    if (match) return match.name.charAt(0).toUpperCase() + match.name.slice(1);
    // Value no longer exists in packagings list → show (κενό)
    return '(κενό)';
  };
  const normalizePackaging = (val) => {
    if (!val) return null;
    const lower = String(val).trim().toLowerCase();
    if (packagingNames.includes(lower)) return lower;
    return null;
  };
  const uncategorizedProducts = products.filter(p => !p.category || !sortedCatNames.includes(p.category));

  // ============================================================
  // SEARCH
  // ============================================================
  // Build ordered list of match keys in the order they appear on screen
  const matches = useMemo(() => {
    const q = normalizeText(search.trim());
    if (!q) return [];
    const keys = [];
    cats.forEach(cat => {
      const catProds = getProductsByCat(cat.name);
      if (catProds.length === 0 && !showInactive) return;
      if (normalizeText(cat.name).includes(q)) keys.push(`cat:${cat.id}`);
      catProds.forEach(p => {
        if (normalizeText(p.name).includes(q)) keys.push(`prod:${p.id}`);
      });
    });
    uncategorizedProducts.forEach(p => {
      if (normalizeText(p.name).includes(q)) keys.push(`prod:${p.id}`);
    });
    return keys;
  }, [search, cats, products, showInactive]);

  useEffect(() => { setCurrentMatch(-1); }, [search]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    if (matches.length === 0) return;
    const nextIdx = (currentMatch + 1) % matches.length;
    const key = matches[nextIdx];
    const el = rowRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightKey(key);
      setTimeout(() => setHighlightKey(null), 1800);
    }
    setCurrentMatch(nextIdx);
  };

  // Duplicate check for product modal (name trimmed, case+accent insensitive)
  const duplicateProduct = useMemo(() => {
    const name = normalizeText(productForm.name.trim());
    if (!name) return null;
    return products.find(p =>
      normalizeText(p.name) === name && (!editProduct || p.id !== editProduct.id)
    );
  }, [productForm.name, products, editProduct]);

  // ============================================================
  // PRODUCT MODAL
  // ============================================================
  const openCreateProduct = () => {
    setEditProduct(null);
    const defaultPkg = packagings[0]?.name || '';
    setProductForm({ ...emptyProductForm, packaging: defaultPkg });
    setShowProductModal(true);
  };

  const openEditProduct = (p) => {
    setEditProduct(p);
    const cat = cats.find(c => c.name === p.category);
    // Keep the existing packaging value even if it no longer exists in the list
    // (UI will show it; user can optionally change it)
    setProductForm({
      name: p.name,
      category_id: cat ? String(cat.id) : '',
      price: p.price,
      packaging: p.packaging || ''
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) return toast.error('Συμπληρώστε όνομα και τιμή');
    if (!productForm.category_id) return toast.error('Επιλέξτε κατηγορία');
    setSavingProduct(true);
    const cat = cats.find(c => c.id === parseInt(productForm.category_id));
    try {
      const data = { name: productForm.name, category: cat?.name || '', price: productForm.price, packaging: productForm.packaging };
      if (editProduct) {
        await updateProduct(selectedFactory.id, editProduct.id, data);
        toast.success('Το προϊόν ενημερώθηκε');
      } else {
        await createProduct(selectedFactory.id, data);
        toast.success('Το προϊόν δημιουργήθηκε');
      }
      setShowProductModal(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSavingProduct(false); }
  };

  const handleToggle = async (p) => {
    try {
      await updateProduct(selectedFactory.id, p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? 'Απενεργοποιήθηκε' : 'Ενεργοποιήθηκε');
      loadAll();
    } catch { toast.error('Σφάλμα'); }
  };

  // ============================================================
  // CATEGORY MANAGEMENT MODAL
  // ============================================================
  const openCatModal = () => { setCatEditId(null); setCatEditName(''); setNewCatName(''); setShowCatModal(true); };
  const startEditCat = (cat) => { setCatEditId(cat.id); setCatEditName(cat.name); };
  const cancelEditCat = () => { setCatEditId(null); setCatEditName(''); };

  const saveEditCat = async (catId) => {
    if (!catEditName.trim()) return toast.error('Το όνομα δεν μπορεί να είναι κενό');
    setSavingCat(true);
    try {
      await updateCategory(selectedFactory.id, catId, { name: catEditName.trim() });
      toast.success('Η κατηγορία ενημερώθηκε');
      setCatEditId(null); setCatEditName('');
      const catRes = await getCategories(selectedFactory.id);
      setCats(catRes.data);
      const prodRes = await getProducts(selectedFactory.id, showInactive);
      setProducts(prodRes.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSavingCat(false); }
  };

  const handleAddCat = async () => {
    if (!newCatName.trim()) return toast.error('Συμπληρώστε όνομα κατηγορίας');
    setSavingCat(true);
    try {
      await createCategory(selectedFactory.id, { name: newCatName.trim() });
      toast.success('Η κατηγορία δημιουργήθηκε');
      setNewCatName('');
      const catRes = await getCategories(selectedFactory.id);
      setCats(catRes.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSavingCat(false); }
  };

  const handleDeleteCat = async (cat) => {
    if (!window.confirm(`Διαγραφή κατηγορίας "${cat.name}";`)) return;
    try {
      await deleteCategory(selectedFactory.id, cat.id);
      toast.success('Η κατηγορία διαγράφηκε');
      const catRes = await getCategories(selectedFactory.id);
      setCats(catRes.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  // Modal category drag (same-size items, live swap works fine)
  const onModalCatDragStart = (e, cat) => {
    modalDragItem.current = cat; modalDragBackup.current = null;
    modalLastTarget.current = null; modalDropDone.current = false;
    setModalDraggingId(cat.id);
    e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onModalCatDragOver = (e, targetCat) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (!modalDragItem.current || modalDragItem.current.id === targetCat.id) return;
    if (modalLastTarget.current === targetCat.id) return;
    setCats(prev => {
      if (!modalDragBackup.current) modalDragBackup.current = [...prev];
      const fi = prev.findIndex(c => c.id === modalDragItem.current.id);
      const ti = prev.findIndex(c => c.id === targetCat.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...prev]; n.splice(fi, 1); n.splice(ti, 0, modalDragItem.current);
      modalLastTarget.current = modalDragItem.current.id;
      return n;
    });
  };
  const onModalCatDrop = (e) => { e.preventDefault(); e.stopPropagation(); modalDropDone.current = true; };
  const onModalCatDragEnd = async () => {
    const wasDrop = modalDropDone.current; const backup = modalDragBackup.current;
    setModalDraggingId(null); modalDragItem.current = null;
    modalLastTarget.current = null; modalDropDone.current = false;
    if (!wasDrop) { if (backup) setCats(backup); modalDragBackup.current = null; return; }
    modalDragBackup.current = null;
    setCats(prev => {
      reorderCategoriesApi(selectedFactory.id, prev.map(c => c.id)).catch(() => {
        toast.error('Σφάλμα αποθήκευσης σειράς'); if (backup) setCats(backup);
      }); return prev;
    });
  };

  // ============================================================
  // PACKAGING MODAL
  // ============================================================
  const openPkgModal = () => { setPkgEditId(null); setPkgEditName(''); setNewPkgName(''); setShowPkgModal(true); };
  const startEditPkg = (pkg) => { setPkgEditId(pkg.id); setPkgEditName(pkg.name); };
  const cancelEditPkg = () => { setPkgEditId(null); setPkgEditName(''); };

  const saveEditPkg = async (pkgId) => {
    if (!pkgEditName.trim()) return toast.error('Το όνομα δεν μπορεί να είναι κενό');
    setSavingPkg(true);
    try {
      await updatePackaging(pkgId, { name: pkgEditName.trim() });
      toast.success('Η συσκευασία ενημερώθηκε');
      setPkgEditId(null); setPkgEditName('');
      // Reload packagings + products (packaging rename cascades to products)
      const pkgRes = await getPackagings();
      setPackagings(pkgRes.data);
      const prodRes = await getProducts(selectedFactory.id, showInactive);
      setProducts(prodRes.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSavingPkg(false); }
  };

  const handleAddPkg = async () => {
    if (!newPkgName.trim()) return toast.error('Συμπληρώστε όνομα συσκευασίας');
    setSavingPkg(true);
    try {
      await createPackaging({ name: newPkgName.trim() });
      toast.success('Η συσκευασία δημιουργήθηκε');
      setNewPkgName('');
      const pkgRes = await getPackagings();
      setPackagings(pkgRes.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
    finally { setSavingPkg(false); }
  };

  const handleDeletePkg = async (pkg) => {
    // First attempt - will fail with 400 if in use, giving us the count
    try {
      await deletePackaging(pkg.id, false);
      toast.success('Η συσκευασία διαγράφηκε');
      const pkgRes = await getPackagings();
      setPackagings(pkgRes.data);
      return;
    } catch (err) {
      const count = err.response?.data?.productsCount;
      if (typeof count === 'number' && count > 0) {
        const confirmMsg = `${count} προϊόντα χρησιμοποιούν τη συσκευασία "${pkg.name}". Αν συνεχίσεις, τα προϊόντα θα εμφανίζονται ως "(κενό)". Συνέχεια;`;
        if (!window.confirm(confirmMsg)) return;
        try {
          await deletePackaging(pkg.id, true);
          toast.success('Η συσκευασία διαγράφηκε');
          const pkgRes = await getPackagings();
          setPackagings(pkgRes.data);
          // Reload products so UI reflects the (κενό) state
          const prodRes = await getProducts(selectedFactory.id, showInactive);
          setProducts(prodRes.data);
        } catch (err2) {
          toast.error(err2.response?.data?.error || 'Σφάλμα');
        }
      } else if (err.response?.status === 404) {
        toast.error('Δεν βρέθηκε');
      } else {
        toast.error(err.response?.data?.error || 'Σφάλμα');
      }
    }
  };

  // Packaging modal drag (live swap) - reuses modal drag refs
  const onModalPkgDragStart = (e, pkg) => {
    modalDragItem.current = pkg; modalDragBackup.current = null;
    modalLastTarget.current = null; modalDropDone.current = false;
    setModalDraggingId(pkg.id);
    e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onModalPkgDragOver = (e, targetPkg) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (!modalDragItem.current || modalDragItem.current.id === targetPkg.id) return;
    if (modalLastTarget.current === targetPkg.id) return;
    setPackagings(prev => {
      if (!modalDragBackup.current) modalDragBackup.current = [...prev];
      const fi = prev.findIndex(c => c.id === modalDragItem.current.id);
      const ti = prev.findIndex(c => c.id === targetPkg.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...prev]; n.splice(fi, 1); n.splice(ti, 0, modalDragItem.current);
      modalLastTarget.current = modalDragItem.current.id;
      return n;
    });
  };
  const onModalPkgDrop = (e) => { e.preventDefault(); e.stopPropagation(); modalDropDone.current = true; };
  const onModalPkgDragEnd = async () => {
    const wasDrop = modalDropDone.current; const backup = modalDragBackup.current;
    setModalDraggingId(null); modalDragItem.current = null;
    modalLastTarget.current = null; modalDropDone.current = false;
    if (!wasDrop) { if (backup) setPackagings(backup); modalDragBackup.current = null; return; }
    modalDragBackup.current = null;
    setPackagings(prev => {
      reorderPackagings(prev.map(p => p.id)).catch(() => {
        toast.error('Σφάλμα αποθήκευσης σειράς'); if (backup) setPackagings(backup);
      });
      return prev;
    });
  };

  // ============================================================
  // BULK UPLOAD
  // ============================================================
  const openUploadModal = () => {
    setUploadCatId('');
    setUploadParsed([]);
    setUploadErrors([]);
    setUploadFileName('');
    setShowUploadModal(true);
  };

  const validateRows = (rows) => {
    const parsed = [];
    const errors = [];

    if (rows.length === 0) {
      errors.push('Το αρχείο είναι κενό');
      return { parsed, errors };
    }
    if (rows.length > MAX_BULK_PRODUCTS) {
      errors.push(`Μέγιστο ${MAX_BULK_PRODUCTS} προϊόντα ανά ανέβασμα (βρέθηκαν ${rows.length})`);
      return { parsed, errors };
    }

    rows.forEach((row, i) => {
      const line = i + 1;
      const rowErrors = [];

      const name = (row[0] || '').trim();
      const packagingRaw = (row[1] || '').trim();
      const priceRaw = (row[2] !== undefined && row[2] !== null) ? String(row[2]).trim() : '';

      if (!name) rowErrors.push('λείπει το όνομα');

      const packaging = normalizePackaging(packagingRaw);
      if (!packaging) {
        if (!packagingRaw) {
          rowErrors.push('λείπει η συσκευασία');
        } else {
          rowErrors.push(`η συσκευασία "${packagingRaw}" δεν υπάρχει (επιτρεπτά: ${packagings.map(p => p.name).join(', ')})`);
        }
      }

      const price = parseFloat(priceRaw.replace(',', '.'));
      if (!priceRaw) {
        rowErrors.push('λείπει η τιμή');
      } else if (isNaN(price) || price < 0) {
        rowErrors.push(`μη έγκυρη τιμή "${priceRaw}"`);
      }

      if (rowErrors.length > 0) {
        errors.push(`Γραμμή ${line}: ${rowErrors.join(', ')}`);
      }

      parsed.push({
        name,
        packaging: packaging || packagingRaw,
        price: isNaN(price) ? priceRaw : price,
        hasError: rowErrors.length > 0
      });
    });

    return { parsed, errors };
  };

  const parseTxtFile = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const rows = [];
    const formatErrors = [];

    lines.forEach((line, i) => {
      const parts = line.split(',');
      if (parts.length < 3) {
        formatErrors.push(`Γραμμή ${i + 1}: "${line}" — πρέπει να έχει 3 πεδία χωρισμένα με κόμμα (βρέθηκαν ${parts.length})`);
        return;
      }
      if (parts.length > 3) {
        formatErrors.push(`Γραμμή ${i + 1}: "${line}" — βρέθηκαν ${parts.length} πεδία αντί για 3`);
        return;
      }
      rows.push(parts.map(p => p.trim()));
    });

    if (formatErrors.length > 0) {
      return { parsed: [], errors: formatErrors };
    }
    return validateRows(rows);
  };

  const parseExcelFile = (data) => {
    try {
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

      // Filter out empty rows
      let rows = jsonRows.filter(r => r.some(cell => cell !== '' && cell !== null && cell !== undefined));

      if (rows.length === 0) return { parsed: [], errors: ['Το αρχείο είναι κενό'] };

      // Auto-detect header row: if first row looks like headers, skip it
      const firstRow = rows[0].map(c => String(c).toLowerCase().trim());
      const headerKeywords = ['όνομα', 'ονομα', 'name', 'συσκευασία', 'συσκευασια', 'packaging', 'τιμή', 'τιμη', 'price'];
      const looksLikeHeader = firstRow.some(cell => headerKeywords.includes(cell));
      if (looksLikeHeader) {
        rows = rows.slice(1);
      }

      // Validate column count
      const formatErrors = [];
      const cleanRows = [];
      rows.forEach((row, i) => {
        const lineNum = looksLikeHeader ? i + 2 : i + 1;
        const cells = row.slice(0, 3);
        if (cells.length < 3 || cells.every(c => c === '' || c === null || c === undefined)) return; // skip empty
        if (cells.filter(c => c !== '' && c !== null && c !== undefined).length < 3) {
          formatErrors.push(`Γραμμή ${lineNum}: λείπουν στήλες — πρέπει να υπάρχουν 3 στήλες (Όνομα, Συσκευασία, Τιμή)`);
          return;
        }
        cleanRows.push(cells.map(c => String(c).trim()));
      });

      if (formatErrors.length > 0) {
        return { parsed: [], errors: formatErrors };
      }
      return validateRows(cleanRows);
    } catch (err) {
      return { parsed: [], errors: ['Δεν ήταν δυνατή η ανάγνωση του αρχείου Excel'] };
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFileName(file.name);
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'txt' || ext === 'csv') {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const { parsed, errors } = parseTxtFile(ev.target.result);
        setUploadParsed(parsed);
        setUploadErrors(errors);
      };
      reader.readAsText(file, 'UTF-8');
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const { parsed, errors } = parseExcelFile(ev.target.result);
        setUploadParsed(parsed);
        setUploadErrors(errors);
      };
      reader.readAsArrayBuffer(file);
    } else {
      setUploadErrors(['Μη αποδεκτός τύπος αρχείου. Επιτρεπτά: .txt, .csv, .xlsx, .xls']);
      setUploadParsed([]);
    }

    // Reset file input so same file can be re-selected
    e.target.value = '';
  };

  const handleBulkUpload = async () => {
    if (!uploadCatId) return toast.error('Επιλέξτε κατηγορία');
    if (uploadErrors.length > 0) return;
    if (uploadParsed.length === 0) return;

    const cat = cats.find(c => c.id === parseInt(uploadCatId));
    if (!cat) return toast.error('Μη έγκυρη κατηγορία');

    setUploadSaving(true);
    try {
      const items = uploadParsed.map(p => ({
        name: p.name,
        packaging: p.packaging,
        price: p.price
      }));
      await bulkCreateProducts(selectedFactory.id, { category: cat.name, products: items });
      toast.success(`Δημιουργήθηκαν ${items.length} προϊόντα!`);
      setShowUploadModal(false);
      loadAll();
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      if (serverErrors) {
        setUploadErrors(serverErrors);
      } else {
        toast.error(err.response?.data?.error || 'Σφάλμα ανεβάσματος');
      }
    } finally {
      setUploadSaving(false);
    }
  };

  // ============================================================
  // PRODUCT DRAG & DROP (main page)
  // ============================================================
  const onProductDragStart = (e, product, catName) => {
    dragType.current = 'product'; dragItem.current = product;
    dragCatName.current = catName; dragBackup.current = null;
    lastTarget.current = null; dropDone.current = false;
    setDraggingProductId(product.id);
    e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onProductDragOver = (e, targetProduct, catName) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (dragType.current !== 'product') return;
    if (!dragItem.current || dragItem.current.id === targetProduct.id) return;
    if (dragCatName.current !== catName) return;
    if (lastTarget.current === targetProduct.id) return;
    setProducts(prev => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const catProds = prev.filter(p => (p.category || '') === catName);
      const otherProds = prev.filter(p => (p.category || '') !== catName);
      const fi = catProds.findIndex(p => p.id === dragItem.current.id);
      const ti = catProds.findIndex(p => p.id === targetProduct.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...catProds]; n.splice(fi, 1); n.splice(ti, 0, dragItem.current);
      lastTarget.current = dragItem.current.id;
      const result = [];
      sortedCatNames.forEach(cn => {
        if (cn === catName) result.push(...n);
        else result.push(...otherProds.filter(p => (p.category || '') === cn));
      });
      result.push(...otherProds.filter(p => !p.category || !sortedCatNames.includes(p.category)));
      return result;
    });
  };
  const onProductDrop = (e) => { e.preventDefault(); e.stopPropagation(); dropDone.current = true; };
  const onProductDragEnd = async () => {
    const wasDrop = dropDone.current; const backup = dragBackup.current;
    const catName = dragCatName.current;
    setDraggingProductId(null); dragType.current = null; dragItem.current = null;
    dragCatName.current = null; lastTarget.current = null; dropDone.current = false;
    if (!wasDrop) { if (backup) setProducts(backup); dragBackup.current = null; return; }
    dragBackup.current = null;
    setProducts(prev => {
      const catProds = prev.filter(p => (p.category || '') === catName);
      reorderProducts(selectedFactory.id, catProds.map(p => p.id)).catch(() => {
        toast.error('Σφάλμα αποθήκευσης σειράς'); if (backup) setProducts(backup);
      }); return prev;
    });
  };

  // ============================================================
  // CATEGORY DRAG & DROP (main page) — DROP INDICATOR approach
  // ============================================================
  const onCatDragStart = (e, cat) => {
    catDragItem.current = cat; catDropDone.current = false;
    setDraggingCatId(cat.id); setCatDropIndicator(null);
    e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', '');
  };
  const onCatDragOver = (e, targetCat) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (!catDragItem.current || catDragItem.current.id === targetCat.id) {
      setCatDropIndicator(null); return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    setCatDropIndicator({ catId: targetCat.id, position });
  };
  const onCatDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setCatDropIndicator(null);
  };
  const onCatDrop = (e, targetCat) => {
    e.preventDefault(); e.stopPropagation(); catDropDone.current = true;
    if (!catDragItem.current || catDragItem.current.id === targetCat.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    const currentOrder = [...cats];
    const fromIdx = currentOrder.findIndex(c => c.id === catDragItem.current.id);
    if (fromIdx === -1) return;
    const draggedCat = currentOrder.splice(fromIdx, 1)[0];
    let toIdx = currentOrder.findIndex(c => c.id === targetCat.id);
    if (toIdx === -1) return;
    if (position === 'below') toIdx += 1;
    currentOrder.splice(toIdx, 0, draggedCat);
    setCats(currentOrder);
    reorderCategoriesApi(selectedFactory.id, currentOrder.map(c => c.id)).catch(() => {
      toast.error('Σφάλμα αποθήκευσης σειράς κατηγοριών'); loadAll();
    });
  };
  const onCatDragEnd = () => {
    setDraggingCatId(null); setCatDropIndicator(null);
    catDragItem.current = null; catDropDone.current = false;
  };

  // ============================================================
  // STYLES
  // ============================================================
  const dragHandleStyle = {
    cursor: 'grab', padding: '0 8px', color: 'var(--gray-300)',
    fontSize: 18, userSelect: 'none', display: 'flex', alignItems: 'center',
  };
  const dropLineStyle = {
    height: 4, background: '#3b82f6', borderRadius: 2, margin: '4px 0',
  };

  // ============================================================
  // RENDER CATEGORY CARD
  // ============================================================
  const getProductCount = (catName) => products.filter(p => p.category === catName).length;

  const renderCategoryCard = (cat) => {
    const catProds = getProductsByCat(cat.name);
    const isCatDragging = draggingCatId === cat.id;
    if (catProds.length === 0 && !showInactive) return null;
    const showAbove = catDropIndicator?.catId === cat.id && catDropIndicator.position === 'above';
    const showBelow = catDropIndicator?.catId === cat.id && catDropIndicator.position === 'below';

    return (
      <div key={cat.id}>
        {showAbove && <div style={dropLineStyle} />}
        <div className="card"
          ref={el => { if (el) rowRefs.current[`cat:${cat.id}`] = el; }}
          style={{
            marginBottom: 16,
            border: highlightKey === `cat:${cat.id}` ? '2px solid #f59e0b' : (isCatDragging ? '2px solid #22c55e' : undefined),
            background: highlightKey === `cat:${cat.id}` ? '#fef3c7' : undefined,
            opacity: isCatDragging ? 0.4 : 1,
            transition: 'background 0.4s ease, border-color 0.4s ease, opacity 0.15s ease',
          }}
          onDragOver={canManage && draggingCatId ? (e) => onCatDragOver(e, cat) : undefined}
          onDragLeave={canManage && draggingCatId ? onCatDragLeave : undefined}
          onDrop={canManage ? (e) => {
            if (catDragItem.current) onCatDrop(e, cat);
            if (dragType.current === 'product') onProductDrop(e);
          } : undefined}
        >
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {canManage && (
              <span draggable onDragStart={(e) => onCatDragStart(e, cat)} onDragEnd={onCatDragEnd}
                style={{ ...dragHandleStyle, color: isCatDragging ? '#22c55e' : 'var(--gray-300)' }}
                title="Σύρετε για αλλαγή σειράς κατηγορίας">⠿</span>
            )}
            <span className="card-title">{cat.name}</span>
            <span className="badge badge-gray" style={{ marginLeft: 'auto' }}>{catProds.length} προϊόντα</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                {canManage && <th style={{ width: 40 }}></th>}
                <th>Όνομα</th><th>Συσκευασία</th><th>Τιμή</th><th>Κατάσταση</th>
                {canManage && <th style={{ textAlign: 'right' }}>Ενέργειες</th>}
              </tr></thead>
              <tbody>
                {catProds.map(p => {
                  const isDragging = draggingProductId === p.id;
                  const isHL = highlightKey === `prod:${p.id}`;
                  return (
                    <tr key={p.id}
                      ref={el => { if (el) rowRefs.current[`prod:${p.id}`] = el; }}
                      style={{ background: isHL ? '#fef3c7' : (isDragging ? '#dcfce7' : ''), opacity: isDragging ? 0.5 : 1, transition: 'background 0.4s ease, opacity 0.15s ease' }}
                      onDragOver={canManage ? (e) => { if (dragType.current === 'product') onProductDragOver(e, p, cat.name); } : undefined}
                      onDrop={canManage ? (e) => { if (dragType.current === 'product') onProductDrop(e); } : undefined}
                    >
                      {canManage && (
                        <td style={{ padding: '8px 0' }}>
                          <span draggable onDragStart={(e) => onProductDragStart(e, p, cat.name)} onDragEnd={onProductDragEnd}
                            style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
                        </td>
                      )}
                      <td><strong>{p.name}</strong></td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{packagingLabel(p.packaging)}</td>
                      <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(p.price).toFixed(2)}</strong></td>
                      <td><span className={`badge ${p.is_active ? 'badge-green' : 'badge-gray'}`}>{p.is_active ? 'Ενεργό' : 'Ανενεργό'}</span></td>
                      {canManage && (
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEditProduct(p)}>✏️ Επεξεργασία</button>
                            <button className={`btn btn-sm ${p.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(p)}>
                              {p.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {showBelow && <div style={dropLineStyle} />}
      </div>
    );
  };

  return (
    <>
      <div className="page-header" style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--gray-50)', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--gray-200)' }}>
        <div className="flex-between">
          <div>
            <h2>Προϊόντα</h2>
            <p>Διαχείριση προϊόντων και τιμών — {selectedFactory?.name}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            {canManage && (
              <>
                <button className="btn btn-secondary" onClick={openCatModal}>+ Νέα Κατηγορία</button>
                <button className="btn btn-secondary" onClick={openPkgModal}>+ Νέα Συσκευασία</button>
                <button className="btn btn-secondary" onClick={openUploadModal}>+ Ανέβασμα Προϊόντων</button>
                <button className="btn btn-primary" onClick={openCreateProduct}>+ Νέο Προϊόν</button>
              </>
            )}
          </div>
        </div>
        {(products.length > 0 || cats.length > 0) && (
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, maxWidth: 560 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="🔍 Αναζήτηση προϊόντος ή κατηγορίας..."
                style={{ paddingRight: search ? 70 : 12, fontSize: 13, height: 34 }}
              />
              {search && (
                <>
                  <span style={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, pointerEvents: 'none' }}>
                    {matches.length > 0 ? `${currentMatch < 0 ? 0 : currentMatch + 1}/${matches.length}` : '0'}
                  </span>
                  <button type="button" onClick={() => setSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 16, lineHeight: 1, padding: 2 }} title="Καθαρισμός">✕</button>
                </>
              )}
            </div>
            <button type="submit" className="btn btn-secondary" disabled={!search || matches.length === 0} style={{ height: 34, padding: '0 14px', fontSize: 13 }}>
              Επόμενο
            </button>
          </form>
        )}
      </div>
      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : cats.length === 0 && products.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h3>Δεν υπάρχουν προϊόντα</h3>
              <p>Δημιουργήστε πρώτα μια κατηγορία και μετά προσθέστε προϊόντα</p>
              {canManage && (
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button className="btn btn-secondary" onClick={openCatModal}>+ Νέα Κατηγορία</button>
                  <button className="btn btn-primary" onClick={openCreateProduct}>+ Νέο Προϊόν</button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {cats.map(cat => renderCategoryCard(cat))}
            {uncategorizedProducts.length > 0 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <span className="card-title">Χωρίς Κατηγορία</span>
                  <span className="badge badge-gray" style={{ marginLeft: 'auto' }}>{uncategorizedProducts.length} προϊόντα</span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Όνομα</th><th>Συσκευασία</th><th>Τιμή</th><th>Κατάσταση</th>{canManage && <th style={{ textAlign: 'right' }}>Ενέργειες</th>}</tr></thead>
                    <tbody>
                      {uncategorizedProducts.map(p => (
                        <tr key={p.id}
                          ref={el => { if (el) rowRefs.current[`prod:${p.id}`] = el; }}
                          style={{ background: highlightKey === `prod:${p.id}` ? '#fef3c7' : undefined, transition: 'background 0.4s ease' }}
                        >
                          <td><strong>{p.name}</strong></td>
                          <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{packagingLabel(p.packaging)}</td>
                          <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(p.price).toFixed(2)}</strong></td>
                          <td><span className={`badge ${p.is_active ? 'badge-green' : 'badge-gray'}`}>{p.is_active ? 'Ενεργό' : 'Ανενεργό'}</span></td>
                          {canManage && (
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => openEditProduct(p)}>✏️ Επεξεργασία</button>
                                <button className={`btn btn-sm ${p.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(p)}>
                                  {p.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* PRODUCT MODAL */}
      {showProductModal && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editProduct ? 'Επεξεργασία Προϊόντος' : 'Νέο Προϊόν'}</span>
              <button className="modal-close" onClick={() => setShowProductModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Όνομα Προϊόντος *</label>
                <input className="form-input" value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })} placeholder="π.χ. Ψωμί Λευκό" />
                {duplicateProduct && (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                    ⚠ Υπάρχει ήδη προϊόν με παρόμοιο όνομα: <strong>{duplicateProduct.name}</strong>
                    {duplicateProduct.category ? ` (${duplicateProduct.category})` : ''}
                    {!duplicateProduct.is_active && ' — Ανενεργό'}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Κατηγορία *</label>
                <select className="form-input" value={productForm.category_id} onChange={e => setProductForm({ ...productForm, category_id: e.target.value })}>
                  <option value="">— Επιλέξτε κατηγορία —</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Συσκευασία *</label>
                <select className="form-input" value={productForm.packaging} onChange={e => setProductForm({ ...productForm, packaging: e.target.value })}>
                  <option value="">— Επιλέξτε —</option>
                  {packagings.map(pkg => (
                    <option key={pkg.id} value={pkg.name}>
                      {pkg.name.charAt(0).toUpperCase() + pkg.name.slice(1)}
                    </option>
                  ))}
                  {/* If product has a packaging that no longer exists in list, keep it as an option so user sees it */}
                  {editProduct && productForm.packaging && !packagings.find(p => p.name.toLowerCase() === productForm.packaging.toLowerCase()) && (
                    <option value={productForm.packaging}>
                      {productForm.packaging} (κενό)
                    </option>
                  )}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Τιμή (€) *</label>
                <input className="form-input" type="number" step="0.01" min="0" value={productForm.price} onChange={e => setProductForm({ ...productForm, price: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProductModal(false)}>Ακύρωση</button>
              <button className="btn btn-primary" onClick={handleSaveProduct} disabled={savingProduct}>
                {savingProduct ? <span className="loading-spinner" /> : (editProduct ? 'Αποθήκευση' : 'Δημιουργία')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CATEGORY MANAGEMENT MODAL */}
      {showCatModal && (
        <div className="modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">Διαχείριση Κατηγοριών</span>
              <button className="modal-close" onClick={() => setShowCatModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              {cats.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--gray-400)' }}>Δεν υπάρχουν κατηγορίες.</div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {cats.map(cat => {
                    const isEditing = catEditId === cat.id;
                    const isDragging = modalDraggingId === cat.id;
                    const prodCount = getProductCount(cat.name);
                    return (
                      <div key={cat.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                        borderBottom: '1px solid var(--gray-100)',
                        background: isDragging ? '#dcfce7' : '', opacity: isDragging ? 0.5 : 1,
                      }}
                        onDragOver={(e) => onModalCatDragOver(e, cat)} onDrop={onModalCatDrop}
                      >
                        <span draggable onDragStart={(e) => onModalCatDragStart(e, cat)} onDragEnd={onModalCatDragEnd}
                          style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)', padding: '0 4px' }}>⠿</span>
                        {isEditing ? (
                          <input className="form-input" value={catEditName} onChange={e => setCatEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEditCat(cat.id); if (e.key === 'Escape') cancelEditCat(); }}
                            autoFocus style={{ flex: 1, margin: 0, padding: '6px 10px', fontSize: 14 }} />
                        ) : (
                          <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{cat.name}</span>
                        )}
                        <span style={{ fontSize: 12, color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{prodCount} {prodCount === 1 ? 'προϊόν' : 'προϊόντα'}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {isEditing ? (<>
                            <button className="btn btn-primary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => saveEditCat(cat.id)} disabled={savingCat}>✓</button>
                            <button className="btn btn-secondary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={cancelEditCat}>✕</button>
                          </>) : (<>
                            <button className="btn btn-secondary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => startEditCat(cat)}>✏️</button>
                            {prodCount === 0 && <button className="btn btn-danger btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDeleteCat(cat)}>🗑</button>}
                          </>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: cats.length > 0 ? '2px solid var(--gray-100)' : 'none' }}>
                <input className="form-input" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddCat(); }}
                  placeholder="Νέα κατηγορία..." style={{ flex: 1, margin: 0, padding: '8px 12px' }} />
                <button className="btn btn-primary" onClick={handleAddCat} disabled={savingCat || !newCatName.trim()}>+ Προσθήκη</button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowCatModal(false); loadAll(); }}>Κλείσιμο</button>
            </div>
          </div>
        </div>
      )}

      {/* PACKAGING MODAL */}
      {showPkgModal && (
        <div className="modal-overlay" onClick={() => setShowPkgModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">Διαχείριση Συσκευασιών</span>
              <button className="modal-close" onClick={() => setShowPkgModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              {packagings.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--gray-400)' }}>Δεν υπάρχουν συσκευασίες.</div>
              ) : (
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {packagings.map(pkg => {
                    const isEditing = pkgEditId === pkg.id;
                    const isDragging = modalDraggingId === pkg.id;
                    const usageCount = products.filter(p => (p.packaging || '').toLowerCase() === pkg.name.toLowerCase()).length;
                    return (
                      <div key={pkg.id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                        borderBottom: '1px solid var(--gray-100)',
                        background: isDragging ? '#dcfce7' : '', opacity: isDragging ? 0.5 : 1,
                      }}
                        onDragOver={(e) => onModalPkgDragOver(e, pkg)} onDrop={onModalPkgDrop}
                      >
                        <span draggable onDragStart={(e) => onModalPkgDragStart(e, pkg)} onDragEnd={onModalPkgDragEnd}
                          style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)', padding: '0 4px' }}>⠿</span>
                        {isEditing ? (
                          <input className="form-input" value={pkgEditName} onChange={e => setPkgEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEditPkg(pkg.id); if (e.key === 'Escape') cancelEditPkg(); }}
                            autoFocus style={{ flex: 1, margin: 0, padding: '6px 10px', fontSize: 14 }} />
                        ) : (
                          <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{pkg.name}</span>
                        )}
                        <span style={{ fontSize: 12, color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{usageCount} {usageCount === 1 ? 'προϊόν' : 'προϊόντα'}</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {isEditing ? (<>
                            <button className="btn btn-primary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => saveEditPkg(pkg.id)} disabled={savingPkg}>✓</button>
                            <button className="btn btn-secondary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={cancelEditPkg}>✕</button>
                          </>) : (<>
                            <button className="btn btn-secondary btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => startEditPkg(pkg)}>✏️</button>
                            <button className="btn btn-danger btn-sm" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDeletePkg(pkg)}>🗑</button>
                          </>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: packagings.length > 0 ? '2px solid var(--gray-100)' : 'none' }}>
                <input className="form-input" value={newPkgName} onChange={e => setNewPkgName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddPkg(); }}
                  placeholder="Νέα συσκευασία..." style={{ flex: 1, margin: 0, padding: '8px 12px' }} />
                <button className="btn btn-primary" onClick={handleAddPkg} disabled={savingPkg || !newPkgName.trim()}>+ Προσθήκη</button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowPkgModal(false); loadAll(); }}>Κλείσιμο</button>
            </div>
          </div>
        </div>
      )}

      {/* BULK UPLOAD MODAL */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <span className="modal-title">Ανέβασμα Προϊόντων</span>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* TUTORIAL */}
              <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '14px 18px', marginBottom: 16, fontSize: 13, lineHeight: 1.7 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>📋 Οδηγίες μορφής αρχείου</div>
                <div style={{ marginBottom: 10 }}>
                  Κάθε γραμμή πρέπει να έχει <strong>3 πεδία</strong>: <strong>Όνομα</strong>, <strong>Συσκευασία</strong>, <strong>Τιμή</strong>
                </div>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>Αρχείο κειμένου (.txt / .csv):</div>
                <div style={{ background: 'var(--gray-800)', color: '#e2e8f0', borderRadius: 6, padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, marginBottom: 12 }}>
                  Ρυζόγαλο, Τεμάχιο, 4.50<br/>
                  Μπουγάτσα, Ταψί, 6.70<br/>
                  Τσουρέκι, Τεμάχιο, 5.70
                </div>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>Αρχείο Excel (.xlsx / .xls):</div>
                <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                  <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-200)' }}>
                        <th style={{ padding: '6px 12px', border: '1px solid var(--gray-300)', textAlign: 'left' }}>Στήλη A</th>
                        <th style={{ padding: '6px 12px', border: '1px solid var(--gray-300)', textAlign: 'left' }}>Στήλη B</th>
                        <th style={{ padding: '6px 12px', border: '1px solid var(--gray-300)', textAlign: 'left' }}>Στήλη C</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>Ρυζόγαλο</td><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>Τεμάχιο</td><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>4.50</td></tr>
                      <tr><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>Μπουγάτσα</td><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>Ταψί</td><td style={{ padding: '6px 12px', border: '1px solid var(--gray-300)' }}>6.70</td></tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>
                  <strong>Συσκευασία:</strong> μόνο «Τεμάχιο» ή «Ταψί» (πεζά/κεφαλαία δεν πειράζει)
                  <br/><strong>Όριο:</strong> μέχρι {MAX_BULK_PRODUCTS} προϊόντα ανά ανέβασμα
                  <br/><strong>Τιμή:</strong> με τελεία ή κόμμα (π.χ. 4.50 ή 4,50)
                </div>
              </div>

              {/* Category selection */}
              <div className="form-group">
                <label className="form-label">Κατηγορία για τα νέα προϊόντα *</label>
                <select className="form-input" value={uploadCatId} onChange={e => setUploadCatId(e.target.value)}>
                  <option value="">— Επιλέξτε κατηγορία —</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* File upload */}
              <div className="form-group">
                <label className="form-label">Αρχείο *</label>
                <input type="file" ref={fileInputRef} accept=".txt,.csv,.xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
                    📁 Επιλογή αρχείου
                  </button>
                  {uploadFileName && <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>{uploadFileName}</span>}
                </div>
              </div>

              {/* Errors */}
              {uploadErrors.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 6, fontSize: 13 }}>❌ Σφάλματα ({uploadErrors.length})</div>
                  <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                    {uploadErrors.map((err, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#991b1b', padding: '2px 0' }}>{err}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview table */}
              {uploadParsed.length > 0 && uploadErrors.length === 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: '#16a34a' }}>
                    ✅ {uploadParsed.length} προϊόντα έτοιμα για ανέβασμα
                  </div>
                  <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                    <table style={{ width: '100%', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--gray-50)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>#</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Όνομα</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Συσκευασία</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Τιμή</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadParsed.map((p, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--gray-100)' }}>
                            <td style={{ padding: '6px 12px', color: 'var(--gray-400)' }}>{i + 1}</td>
                            <td style={{ padding: '6px 12px', fontWeight: 500 }}>{p.name}</td>
                            <td style={{ padding: '6px 12px' }}>{packagingLabel(p.packaging)}</td>
                            <td style={{ padding: '6px 12px' }}>€{parseFloat(p.price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>Ακύρωση</button>
              <button
                className="btn btn-primary"
                onClick={handleBulkUpload}
                disabled={uploadSaving || uploadErrors.length > 0 || uploadParsed.length === 0 || !uploadCatId}
              >
                {uploadSaving ? <span className="loading-spinner" /> : `Ανέβασμα ${uploadParsed.length > 0 ? `(${uploadParsed.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
